import { windowIsReady } from '@/helpers/windowIsReady';

interface RddRuntime {
	isMobile?: boolean;
	isTablet?: boolean;
	isDesktop?: boolean;
	isBrowser?: boolean;

	isAndroid?: boolean;
	isIOS?: boolean;
	isSafari?: boolean;

	isMobileOnly?: boolean;
	isEmbedded?: boolean;

	deviceType?: string;
	browserName?: string;
	osName?: string;

	getSelectorsByUserAgent?: (ua: string) => Record<string, unknown>;
	parseUserAgent?: (ua: string) => unknown;
}

let modPromise: Promise<RddRuntime> | null = null;

async function loadRdd() {
	if (!windowIsReady) return null;

	if (!modPromise) {
		modPromise = import('react-device-detect');
	}

	return await modPromise;
}

export const DeviceDetectService = {
	prefetch() {
		void loadRdd();
	},

	async getRuntime(): Promise<RddRuntime | null> {
		return await loadRdd();
	},

	async isMobile(): Promise<boolean> {
		const rdd = await loadRdd();
		return Boolean(rdd?.isMobile);
	},

	async isDesktop(): Promise<boolean> {
		const rdd = await loadRdd();
		return Boolean(rdd?.isDesktop);
	},

	async isTablet(): Promise<boolean> {
		const rdd = await loadRdd();
		return Boolean(rdd?.isTablet);
	},

	async isEmbedded(): Promise<boolean> {
		const rdd = await loadRdd();
		return Boolean(rdd?.isEmbedded);
	},

	async getInfo(): Promise<{
		isMobile: boolean;
		isTablet: boolean;
		isDesktop: boolean;
		isBrowser: boolean;
		isAndroid: boolean;
		isIOS: boolean;
		isMobileOnly: boolean;
		isEmbedded: boolean;
		deviceType?: string;
		browserName?: string;
		osName?: string;
		ua?: string;
	}> {
		const rdd = await loadRdd();

		return {
			isMobile: Boolean(rdd?.isMobile),
			isTablet: Boolean(rdd?.isTablet),
			isDesktop: Boolean(rdd?.isDesktop),
			isBrowser: Boolean(rdd?.isBrowser),
			isAndroid: Boolean(rdd?.isAndroid),
			isIOS: Boolean(rdd?.isIOS),
			isMobileOnly: Boolean(rdd?.isMobileOnly),
			isEmbedded: Boolean(rdd?.isEmbedded),
			deviceType: rdd?.deviceType,
			browserName: rdd?.browserName,
			osName: rdd?.osName,
			ua: window.navigator.userAgent,
		};
	},

	async iosSortedPaymentMethods(): Promise<boolean> {
		const rdd = await loadRdd();
		return Boolean(rdd?.isIOS);
	},
	async isApplePay(): Promise<boolean> {
		const rdd = await loadRdd();
		return Boolean(rdd?.isSafari || rdd?.isIOS);
	},
	async isOnlyAndroid(): Promise<boolean> {
		const rdd = await loadRdd();
		return Boolean(rdd?.isAndroid && rdd?.isMobile);
	},
	async isInAppBrowser(
		inAppBrowserList = ['messenger', 'facebook', 'instagram', 'twitter'],
	) {
		const InApp = (await import('detect-inapp')).default;
		const { browser } = windowIsReady
			? new InApp(
					navigator.userAgent || navigator.vendor || (window as any).opera,
			  )
			: {
					browser: '',
			  };

		return inAppBrowserList.includes(browser);
	},
	async getSelectorsByUserAgent(ua: string): Promise<Record<string, unknown>> {
		const rdd = await loadRdd();
		if (!rdd?.getSelectorsByUserAgent) return {};
		return rdd?.getSelectorsByUserAgent(ua);
	},
};
